package com.bikompanion.ui.composables

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.bikompanion.ui.mapper.BikeScoreSummaryUiModel
import com.bikompanion.ui.mapper.BikeScoreTone
import com.bikompanion.ui.mapper.BikeScoreVerdict
import com.bikompanion.ui.preview.PreviewSurface
import com.bikompanion.ui.strings.BikompanionStrings
import com.bikompanion.ui.strings.LocalStrings
import com.bikompanion.ui.theme.LocalDarkTheme

/**
 * What a looked-up bike's votes came to, in a line a rider can act on before unlocking it.
 *
 * A bare percentage lies at low vote counts — one grumpy rider makes a fine bike read "0%
 * positive" — so the verdict is only stated once there are a few votes behind it, and the
 * count it rests on is always shown.
 */
@Composable
internal fun BikeScoreSummary(summary: BikeScoreSummaryUiModel) {
    val strings = LocalStrings.current
    // The card follows the theme, so the verdict has to as well: the dark accents that read well
    // on it in light theme all but disappear on its dark-theme surface.
    val darkTheme = LocalDarkTheme.current
    val color = when (summary.tone) {
        BikeScoreTone.Positive -> if (darkTheme) GoodVoteColor else GoodVoteContentColor
        BikeScoreTone.Negative -> if (darkTheme) BadVoteColor else BadVoteContentColor
        BikeScoreTone.Neutral -> MaterialTheme.colorScheme.onSurfaceVariant
    }
    Card(colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(2.dp),
        ) {
            Text(
                text = strings.scoreHeadline(summary.bikeNumber, summary.verdictText(strings)),
                style = MaterialTheme.typography.titleSmall,
                color = color,
            )
            if (summary.ratingCount > 0) {
                Text(
                    text = strings.scorePositiveRides(summary.percentPositive, summary.ratingCount),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

private fun BikeScoreSummaryUiModel.verdictText(strings: BikompanionStrings): String =
    when (verdict) {
        BikeScoreVerdict.NoRatings -> strings.scoreNoRatings
        BikeScoreVerdict.FewRatings -> strings.scoreFewRatings(ratingCount)
        BikeScoreVerdict.LooksGood -> strings.scoreLooksGood
        BikeScoreVerdict.HasProblems -> strings.scoreHasProblems
        BikeScoreVerdict.Mixed -> strings.scoreMixed
    }

private val PreviewSummaries = listOf(
    BikeScoreSummaryUiModel("1234", BikeScoreVerdict.NoRatings, BikeScoreTone.Neutral, percentPositive = 0, ratingCount = 0),
    BikeScoreSummaryUiModel("1234", BikeScoreVerdict.FewRatings, BikeScoreTone.Neutral, percentPositive = 100, ratingCount = 2),
    BikeScoreSummaryUiModel("1234", BikeScoreVerdict.LooksGood, BikeScoreTone.Positive, percentPositive = 92, ratingCount = 25),
    BikeScoreSummaryUiModel("1234", BikeScoreVerdict.HasProblems, BikeScoreTone.Negative, percentPositive = 20, ratingCount = 15),
    BikeScoreSummaryUiModel("1234", BikeScoreVerdict.Mixed, BikeScoreTone.Neutral, percentPositive = 55, ratingCount = 20),
)

@Preview
@Composable
private fun BikeScoreSummaryPreview() {
    PreviewSurface {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            PreviewSummaries.forEach { BikeScoreSummary(it) }
        }
    }
}
